import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import PrintIcon from "@mui/icons-material/Print";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import { useEffect, useMemo, useState } from "react";
import EditTransferModal, {
  COLORS,
} from "../../components/Inventory/EditTransferDrawer";
import {
  StoreTransferList,
  approveStoreRequest,
  fetchProductList,
  fetchStoreList,
  rejectRequest,
} from "../../services/inventoryService";

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { exportRequestExcel } from "../../components/Pdf/exportRequestExcel";
import { printRequest } from "../../components/Pdf/printPdf";
import { RootState } from "../../store";
import theme from "../../theme/theme";

interface TransferRow {
  invoice_no: string;
  store: string;
  receiving_store: string;
  sending_store: string;
  notes: string;
  requested_by: string;
  date: string;
}

enum UserRole {
  ADMIN = "ADMIN",
  SUPERADMIN = "SUPERADMIN",
}

const StoreRequest: React.FC = () => {
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState<any>({
    status: "PENDING",
    page: 1,
    limit: 10,
  });

  const [page, setPage] = useState(1);
  const [expandedCategories, setExpandedCategories] = useState<string | null>(
    null,
  );
  const [products, setProducts] = useState<any[]>([]);
  const [productList, setProductList] = useState<any[]>([]);
  const [storeList, setStoreList] = useState<TransferRow[]>([]);
  const [stores, setStores] = useState<any[]>([]);

  // drawer state (clean single source of truth)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<any>(null);
  const [selectedInvoiceProducts, setSelectedInvoiceProducts] = useState<any[]>(
    [],
  );
  const authUser = useSelector((state: RootState) => state.auth.user);
  const userRole = useMemo(() => {
    return authUser?.role;
  }, [authUser]);
  function getCostMsrpAccess() {
    // allow Admin and SuperAdmin only
    return (
      userRole?.toLocaleLowerCase() === "admin" ||
      userRole?.toLocaleLowerCase() === "superadmin"
    );
  }

  const isMobile = useMediaQuery("(max-width:600px)");

  const getWarehouse = async () => {
    try {
      const data = await fetchStoreList("WAREHOUSE");

      setStores(
        data.map((item: any) => ({
          label: item.name,
          value: item.store_id,
        })),
      );
    } finally {
      setLoading(false);
    }
  };

  const getTransferList = async () => {
    try {
      const res = await StoreTransferList(filter);

      const unique = new Map();

      res.data.forEach((item: any) => {
        if (!unique.has(item.invoice_no)) {
          unique.set(item.invoice_no, {
            invoice_no: item.invoice_no,
            store: item.store,
            receiving_store: item.receiving_store,
            sending_store: item.sending_store,
            notes: item.notes,
            requested_by: item.requested_by,
            date: `${item.created_at.split("T")[0]} ${
              item.created_at.split("T")[1].split(".")[0]
            }`,
          });
        }
      });

      setStoreList(Array.from(unique.values()));

      setProducts(res.data);

      setFilter((prev: any) => ({
        ...prev,
        page: res.pagination.page,
        limit: res.pagination.limit,
        totalPages: res.pagination.totalPages,
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    const data = await fetchProductList("");
    setProductList(data);
  };

  useEffect(() => {
    getWarehouse();
    fetchProducts();
  }, []);

  useEffect(() => {
    getTransferList();
  }, [filter.status, filter.page, filter.limit]);

  const openEditor = (invoice: any) => {
    const matched = products.filter(
      (p: any) => p.invoice_no === invoice.invoice_no,
    );

    setEditingInvoice(invoice);
    setSelectedInvoiceProducts(matched);
    setDrawerOpen(true);
  };

  const closeEditor = () => {
    setDrawerOpen(false);
    setEditingInvoice(null);
    setSelectedInvoiceProducts([]);
    setExpandedCategories(null);
  };

  const refresh = async () => {
    await getTransferList();
  };

  const handleApprove = async (item: any) => {
    // check if all products under the invoice are selected, if not show alert and return
    const confirm = window.confirm(
      "Are you sure, you want to approve the transfer?",
    );
    if (!confirm) {
      return;
    }
    const payload = products.filter((p) => p.invoice_no === item.invoice_no);
    const allSelected = payload.every((p) => p.picked);
    if (!allSelected) {
      alert("Please pick all products before approving the request.");
      return;
    }
    // await approveRequest(payload);
    await approveStoreRequest(item.invoice_no);
    await refresh();
  };

  const handleReject = async (item: any) => {
    const confirm = window.confirm(
      "Are you sure, you want to reject the transfer?",
    );
    if (!confirm) {
      return;
    }
    const payload = products.filter((p) => p.invoice_no === item.invoice_no);

    await rejectRequest(payload);
    await refresh();
  };

  const handlePrint = async (item: any) => {
    const payload = products
      .filter((p) => p.invoice_no === item.invoice_no)
      .sort((a: any, b: any) => a.category?.localeCompare(b.category))
      .sort((a: any, b: any) => a.sub_category?.localeCompare(b.sub_category));

    // allow only admin and super admin to see with cost and msrp
    const showCost = [UserRole.ADMIN, UserRole.SUPERADMIN].includes(
      authUser?.role?.toUpperCase() as UserRole,
    );

    await printRequest(payload, item.notes, undefined, showCost);
  };

  const handleExportExcel = async (item: any) => {
    const payload = products
      .filter((p) => p.invoice_no === item.invoice_no)
      .sort((a: any, b: any) => a.category?.localeCompare(b.category))
      .sort((a: any, b: any) => a.sub_category?.localeCompare(b.sub_category));

    const showCostMsrp = getCostMsrpAccess();
    const showMsrp = getCostMsrpAccess();
    await exportRequestExcel(payload, showCostMsrp, showMsrp, item.notes);
  };

  const setStatus = (status: string) => {
    setFilter((prev: any) => ({
      ...prev,
      status,
    }));
  };

  return (
    <Box sx={{ padding: 1 }}>
      {loading ? (
        <CircularProgress sx={{ display: "block", mx: "auto" }} />
      ) : (
        <>
          <TableContainer component={Paper} sx={{ height: "82vh" }}>
            {isMobile ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {storeList.map((item, index) => (
                  <Paper
                    key={item.invoice_no}
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: "16px",
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        onClick={() => openEditor(item)}
                        sx={{
                          cursor: "pointer",
                          // border: "1px solid gray",
                          backgroundColor: "#10551f",
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.4)",
                          borderRadius: 2,
                          p: 1,
                        }}
                      >
                        <Stack direction={"column"} spacing={1}>
                          <Stack direction={"row"} spacing={1}>
                            <TextSnippetOutlinedIcon sx={{ color: "white" }} />
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: 16,
                                color: "white",
                              }}
                            >
                              Invoice #{item.invoice_no}
                            </Typography>
                          </Stack>
                          <Stack direction={"row"} spacing={2}>
                            <Stack direction={"row"} spacing={0.6}>
                              <CalendarTodayOutlinedIcon
                                sx={{ color: "white", fontSize: 14 }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: "white",
                                }}
                              >
                                {item.date.split(" ")[0]}
                              </Typography>
                            </Stack>
                            <Stack direction={"row"} spacing={0.6}>
                              <AccessTimeOutlinedIcon
                                sx={{ color: "white", fontSize: 14 }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: "white",
                                }}
                              >
                                {item.date.split(" ")[1]}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Box>
                      <IconButton
                        onClick={() => handlePrint(item)}
                        sx={{
                          // border: `1px solid ${theme.palette.primary.main}`,
                          borderRadius: 1,
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.4)",
                        }}
                      >
                        <PrintIcon color={"primary"} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleExportExcel(item)}
                        sx={{
                          // border: `1px solid ${theme.palette.primary.main}`,
                          borderRadius: 1,
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.4)",
                        }}
                      >
                        <BrowserUpdatedIcon color={"primary"} />
                      </IconButton>
                    </Box>
                    <Divider
                      sx={{
                        my: 1.5,
                      }}
                    />
                    {/* Details */}
                    <Box
                      sx={{
                        gap: 1.5,
                      }}
                    >
                      <Stack
                        direction={"row"}
                        spacing={1}
                        alignItems={"center"}
                      >
                        <StorefrontIcon
                          sx={{
                            color: theme.palette.primary.main,
                            backgroundColor: "#efffee",
                            p: 0.6,
                            borderRadius: 10,
                          }}
                        />
                        <Box sx={{ width: "100%", flexGrow: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: COLORS.textSoft,
                            }}
                          >
                            Store
                          </Typography>
                          <Typography fontWeight={600}>{item.store}</Typography>
                        </Box>
                      </Stack>
                      <Stack
                        direction={"row"}
                        spacing={1}
                        alignItems={"center"}
                      >
                        <EditNoteIcon
                          sx={{
                            color: theme.palette.primary.main,
                            backgroundColor: "#efffee",
                            p: 0.6,
                            borderRadius: 10,
                          }}
                        />
                        <Box
                          sx={{
                            gridColumn: "1 / -1",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: COLORS.textSoft,
                            }}
                          >
                            Notes
                          </Typography>
                          <Typography>{item.notes || "-"}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                    {/* Actions */}
                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        disabled={filter.status !== "PENDING"}
                        onClick={() => handleApprove(item)}
                        sx={{ display: "flex", flexDirection: "row", gap: 1 }}
                      >
                        <CheckCircleOutlinedIcon sx={{ fontSize: "small" }} />
                        Approve
                      </Button>
                      <Button
                        fullWidth
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={filter.status !== "PENDING"}
                        onClick={() => handleReject(item)}
                        sx={{ display: "flex", flexDirection: "row", gap: 1 }}
                      >
                        <CancelOutlinedIcon sx={{ fontSize: "small" }} />
                        Reject
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>No</TableCell>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Store</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>PDF</TableCell>
                    <TableCell>Excel</TableCell>
                    <TableCell>Approve</TableCell>
                    <TableCell>Reject</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storeList.map((item, index) => (
                    <TableRow key={item.invoice_no} hover>
                      <TableCell onClick={() => openEditor(item)}>
                        {(filter.page - 1) * 10 + index + 1}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          sx={{
                            cursor: "pointer",
                            backgroundColor: "#10551f",
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.4)",
                            borderRadius: 2,
                            p: 1,
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                            alignItems: "center",
                          }}
                          onClick={() => openEditor(item)}
                        >
                          <StorefrontIcon sx={{ color: "white", mr: 1 }} />#
                          {item.invoice_no}
                        </Button>
                      </TableCell>
                      <TableCell onClick={() => openEditor(item)}>
                        {item.store}
                      </TableCell>
                      <TableCell onClick={() => openEditor(item)}>
                        {item.notes}
                      </TableCell>
                      <TableCell onClick={() => openEditor(item)}>
                        <Stack direction={"row"} spacing={2}>
                          <Stack direction={"row"} spacing={0.6}>
                            <CalendarTodayOutlinedIcon
                              sx={{
                                color: theme.palette.primary.main,
                                fontSize: 17,
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: 14,
                                // color: "white",
                              }}
                            >
                              {item.date.split(" ")[0]}
                            </Typography>
                          </Stack>
                          <Stack direction={"row"} spacing={0.6}>
                            <AccessTimeOutlinedIcon
                              sx={{
                                color: theme.palette.primary.main,
                                fontSize: 17,
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: 14,
                                // color: "white",
                              }}
                            >
                              {item.date.split(" ")[1]}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => handlePrint(item)}>
                          <PrintIcon
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleExportExcel(item)}>
                          <BrowserUpdatedIcon
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={filter.status !== "PENDING"}
                          onClick={() => handleApprove(item)}
                          sx={{ display: "flex", flexDirection: "row", gap: 1 }}
                        >
                          <CheckCircleOutlinedIcon sx={{ fontSize: 17 }} />
                          Approve
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          disabled={filter.status !== "PENDING"}
                          onClick={() => handleReject(item)}
                          sx={{ display: "flex", flexDirection: "row", gap: 1 }}
                        >
                          <CancelOutlinedIcon sx={{ fontSize: 17 }} />
                          Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          {/* Pagination */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <Box></Box>
            <Pagination
              count={filter.totalPages}
              page={filter.page}
              siblingCount={0}
              boundaryCount={0}
              onChange={(_, p) =>
                setFilter((prev: any) => ({
                  ...prev,
                  page: p,
                }))
              }
            />
            <Box>
              <FormControl
                size="small"
                sx={{
                  minWidth: 150,
                }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  value={filter.status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Drawer */}
          <EditTransferModal
            open={drawerOpen}
            onClose={closeEditor}
            onSaved={refresh}
            invoice={editingInvoice}
            products={productList}
            setProducts={setProducts}
            selectedProducts={selectedInvoiceProducts}
            setSelectedProducts={setSelectedInvoiceProducts}
            expandedCategories={expandedCategories}
            setExpandedCategories={setExpandedCategories}
            disabledQty={filter.status !== "PENDING"}
          />
        </>
      )}
    </Box>
  );
};

export default StoreRequest;
